import type { AuthConfigResponse } from "@checklist/shared";

export type SignupMode = AuthConfigResponse["signupMode"];
export type SignupCta =
  | "primary"
  | "nav"
  | "login"
  | "save"
  | "generate"
  | "start"
  | "publish"
  | "aiCreate"
  | "invite";

const CLOSED = "Sign-up is closed";

type Row = { open: string; invite: string; closed: string; neutral: string };

const TABLE: Record<SignupCta, Row> = {
  primary: { open: "Start free", invite: "Request access", closed: CLOSED, neutral: "Get started" },
  nav: { open: "Get started", invite: "Request access", closed: CLOSED, neutral: "Get started" },
  login: {
    open: "Create an account",
    invite: "Request access",
    closed: CLOSED,
    neutral: "Create an account",
  },
  save: {
    open: "Sign up to save this checklist",
    invite: "Request access to save this checklist",
    closed: CLOSED,
    neutral: "Get started",
  },
  generate: {
    open: "Sign up to generate it",
    invite: "Request access to generate it",
    closed: CLOSED,
    neutral: "Get started",
  },
  start: {
    open: "Sign up to start this checklist",
    invite: "Request access to start this checklist",
    closed: CLOSED,
    neutral: "Get started",
  },
  publish: {
    open: "Sign up to publish a list and share it with everyone.",
    invite: "Request access to publish a list and share it with everyone.",
    closed: "Sign-up is closed, so publishing is not available yet.",
    neutral: "Sign up to publish a list and share it with everyone.",
  },
  aiCreate: {
    open: "Sign up to create with AI",
    invite: "Request access to create with AI",
    closed: CLOSED,
    neutral: "Get started",
  },
  invite: {
    open: "Create an account",
    invite: "Request access",
    closed: CLOSED,
    neutral: "Create an account",
  },
};

/** Button/link text for a sign-up CTA. `mode` null = not loaded yet (also SSR). */
export function signupLabel(cta: SignupCta, mode: SignupMode | null): string {
  const row = TABLE[cta];
  if (mode === "open") return row.open;
  if (mode === "invite-only") return row.invite;
  if (mode === "closed") return row.closed;
  return row.neutral;
}

/** Supporting line under the landing CTA band, or null to hide it. */
export function signupTagline(mode: SignupMode | null): string | null {
  if (mode === "open") return "Start free - no credit card, about a minute to your first checklist.";
  if (mode === "invite-only") {
    return "NeatTick is invite-only right now. Request access and we will email you when a spot opens.";
  }
  if (mode === "closed") return null;
  return "Free. No credit card.";
}

/** Body line for a marketing CTA band; never null (D9). */
export function signupBandBody(mode: SignupMode | null): string {
  return signupTagline(mode) ?? "Sign-up is closed right now. You can still browse the community library.";
}
